import Icon from '@components/dataDisplay/Icons/Icon';
import { CustomButtonBase } from '@components/dataEntry/button/ButtonStyle';
import { validateGif } from '@utils/validateGif';
import React, { CSSProperties, useRef } from 'react';
import styled, { css } from 'styled-components';

const customStyle = css`
  width: 100%;
  height: auto;
`;

const UploadFileButton = styled.button<{
  customType: 'icon' | 'button' | 'custom';
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ customType }) => customType === 'custom' && customStyle}

  label {
    ${({ customType }) => customType === 'custom' && customStyle}
    border-radius: var(--buttonBorderRadius);
    corner-shape: var(--buttonCornerShape);

    &:hover {
      cursor: var(--hoverCursorURL), pointer;
    }
  }

  // 기존 파일 업로더 숨기기
  input[type='file'] {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }
`;

export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export const getBase24 = (
  file,
): Promise<string | undefined | null | ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => resolve(e.target?.result);
  });

export const convertToWebp = async (
  imgSrc,
  forBlob,
): Promise<File | Blob | string | undefined> => {
  // Convert image to webp ObjectURL via a canvas blob
  const image: any = await createImage(imgSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (image && ctx != null) {
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);
  }

  if (forBlob) {
    return new Promise((resolve) => {
      canvas.toBlob((blobFile) => {
        if (blobFile != null) resolve(blobFile);
      }, 'image/webp');
    });
  }
  // As a blob
  if (!forBlob) {
    return new Promise((resolve) => {
      canvas.toBlob((file) => {
        resolve(URL.createObjectURL(file!));
      }, 'image/webp');
    });
  }
};

interface UploadInputProps {
  id: string;
  setSelectedFile: any;
  setPreviewImgURL?: any;
  setIsUploading?: any;
  children?: JSX.Element | JSX.Element[];
  accept?: string;
  type?: 'icon' | 'button' | 'custom';
  style?: CSSProperties;
  customSetPreviewImgURL?: any;
}

function UploadInput({
  id,
  type = 'icon',
  setSelectedFile,
  setIsUploading,
  setPreviewImgURL,
  children,
  accept,
  style,
  customSetPreviewImgURL,
}: UploadInputProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  async function processWebpFile(file) {
    if (!file) return;

    if (setIsUploading) setIsUploading(true);
    const base24URL = await getBase24(file);
    const convertWebpFile = await convertToWebp(base24URL, true);
    const previewURL = await convertToWebp(base24URL, false);
    // 디버깅
    setSelectedFile(convertWebpFile);
    setPreviewImgURL(previewURL);
    if (customSetPreviewImgURL) customSetPreviewImgURL(previewURL);
    if (setIsUploading && previewURL) setIsUploading(false);
  }

  async function processNormalFile(file) {
    if (!file) return;
    if (setIsUploading) setIsUploading(true);
    const base24URL = await getBase24(file);
    setSelectedFile(file);
    setPreviewImgURL(base24URL);
    if (setIsUploading && base24URL) setIsUploading(false);
  }

  function handleFileInputChange(e: any) {
    e.preventDefault();
    const file = e.target.files[0];
    // 디버깅
    // console.log(file);

    // 파일 용량 제한
    const fileSize = file.size;
    const limit = 51000000; //50MB
    if (fileSize > limit) {
      alert('파일용량은 50mb 를 넘을수 없습니다.');
      return;
    }
    const isGif = validateGif(file);
    const isSvg = file.type === 'image/svg+xml';

    if (accept || isGif || isSvg) {
      processNormalFile(file);
    } else {
      processWebpFile(file);
    }
  }

  const typeMap = {
    icon: <Icon name="Photo20" />,
    button: (
      <CustomButtonBase size="md" shape="filled">
        이미지 업로드
      </CustomButtonBase>
    ),
    custom: <>{children}</>,
  };

  return (
    <div style={{ display: 'contents', ...style }}>
      <UploadFileButton customType={type}>
        <label htmlFor={id}>{typeMap[type]}</label>
        <input
          type="file"
          ref={fileRef}
          id={id}
          value=""
          accept={accept || 'image/*'}
          onChange={handleFileInputChange}
          multiple
        />
      </UploadFileButton>
    </div>
  );
}

export default UploadInput;
